import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { PROLONGED_RECONNECTING_MS, prolongedRemainingMs, type SessionHealth } from '@joshua/supabase-guard';
import { onHealthRecovered, refreshHealth } from '@/lib/refreshHealth';

export { PROLONGED_RECONNECTING_MS };

/**
 * Session health for components (JOSH-89): `{ status: 'ok' }` or
 * `{ status: 'reconnecting', since }` while the token refresh is in a 429
 * cooldown.
 */
export function useRefreshHealth(): SessionHealth {
  return useSyncExternalStore(refreshHealth.subscribe, refreshHealth.getSnapshot, refreshHealth.getSnapshot);
}

/**
 * True once the current reconnecting stretch has lasted `thresholdMs`
 * (counted from the snapshot's `since`, both on the device clock).
 */
export function useProlongedReconnecting(
  health: SessionHealth,
  thresholdMs: number = PROLONGED_RECONNECTING_MS,
): boolean {
  const since = health.status === 'reconnecting' ? health.since : null;
  const [elapsedFor, setElapsedFor] = useState<number | null>(null);

  useEffect(() => {
    if (since === null) return;
    let cancelled = false;
    const remaining = prolongedRemainingMs({ status: 'reconnecting', since }, Date.now(), thresholdMs) ?? 0;
    if (remaining <= 0) {
      setElapsedFor(since);
      return;
    }
    const id = setTimeout(() => {
      if (!cancelled) setElapsedFor(since);
    }, remaining);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [since, thresholdMs]);

  return since !== null && elapsedFor === since;
}

/**
 * Calls the latest `reload` each time health moves from `reconnecting` back
 * to `ok`, for as long as the caller is mounted. `reload` decides for itself
 * whether anything was paused and needs to run again.
 */
export function useReloadOnReconnect(reload: () => void): void {
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  useEffect(() => {
    let cancelled = false;
    const unsubscribe = onHealthRecovered(() => {
      if (!cancelled) reloadRef.current();
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);
}
